/**
 * Profile URL Utilities
 * 
 * Canonical profile route: /u/:identifier
 * Where identifier can be: handle (preferred) or user_id (fallback)
 * 
 * This module ensures consistent profile linking across the entire platform.
 */

/**
 * Generate a safe profile URL for a user
 * @param {object|string} user - User object or handle/userId string
 * @returns {string} Safe profile URL, never returns invalid routes
 */
export function getProfileUrl(user) {
  if (!user) {
    console.warn('[ProfileUtils] getProfileUrl called with null/undefined user');
    return '/discover'; // Safe fallback
  }

  // If passed a string, assume it's a handle or userId
  if (typeof user === 'string') {
    const identifier = user.trim();
    if (!identifier || identifier === 'undefined' || identifier === 'null') {
      console.warn('[ProfileUtils] Invalid string identifier:', user);
      return '/discover';
    }
    return `/u/${identifier}`;
  }

  // Extract identifier from user object
  // Priority: handle > user_id
  const handle = user.handle || user.username;
  const userId = user.user_id || user.userId || user.id;

  if (handle && handle !== 'undefined' && handle !== 'null' && handle.trim()) {
    return `/u/${handle.trim().toLowerCase()}`;
  }

  if (userId && userId !== 'undefined' && userId !== 'null') {
    // Use user_id as fallback with special prefix
    return `/u/id/${userId}`;
  }

  console.warn('[ProfileUtils] User has no valid identifier:', user);
  return '/discover';
}

/**
 * Check if a profile URL is valid
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export function isValidProfileUrl(url) {
  if (!url || typeof url !== 'string') return false;
  
  // Check for invalid patterns
  const invalidPatterns = [
    /\/u\/$/, // /u/ with nothing after
    /\/u\/undefined/,
    /\/u\/null/,
    /\/profile\/$/, // /profile/ with nothing after
    /\/profile\/undefined/,
    /\/profile\/null/,
    /\/@$/,  // /@ with nothing after
    /\/@undefined/,
    /\/@null/
  ];

  return !invalidPatterns.some(pattern => pattern.test(url));
}

/**
 * Get display handle for a user (with @ prefix)
 * @param {object} user - User object
 * @returns {string} Display handle like "@username" or fallback
 */
export function getDisplayHandle(user) {
  if (!user) return '@user';
  
  // Check all possible handle field names from different API responses
  const handle = user.handle || user.author_handle || user.username || user.display_handle;
  
  if (handle && handle !== 'undefined' && handle !== 'null' && handle.trim()) {
    // Ensure we don't add double @
    const cleanHandle = handle.trim().toLowerCase().replace(/^@/, '');
    return `@${cleanHandle}`;
  }

  // Fallback: generate from name or use user_id
  if (user.name || user.author_name) {
    const name = user.name || user.author_name;
    return `@${name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 15)}`;
  }

  if (user.user_id) {
    return `@user_${user.user_id.slice(-6)}`;
  }

  return '@user';
}

/**
 * Extract identifier from profile URL path
 * @param {string} path - URL path like /u/username or /u/id/user_123
 * @returns {object} { type: 'handle'|'userId', value: string }
 */
export function parseProfilePath(path) {
  if (!path) return null;

  // Handle /u/id/:userId pattern
  const userIdMatch = path.match(/\/u\/id\/([^\/]+)/);
  if (userIdMatch) {
    return { type: 'userId', value: userIdMatch[1] };
  }

  // Handle /u/:handle, /@:handle, /profile/:handle patterns
  const handleMatch = path.match(/(?:\/u\/|\/profile\/|\/@)([^\/]+)/);
  if (handleMatch) {
    return { type: 'handle', value: handleMatch[1] };
  }

  return null;
}

/**
 * Generate a unique handle from user data
 * Used for backfilling users without handles
 * @param {object} user - User object with name, email, or user_id
 * @returns {string} Generated handle
 */
export function generateHandleFromUser(user) {
  if (!user) return `user_${Date.now()}`;

  // Try to create from name
  if (user.name) {
    const base = user.name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .slice(0, 12);
    
    if (base.length >= 3) {
      const suffix = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
      return `${base}${suffix}`;
    }
  }

  // Try to create from email
  if (user.email) {
    const emailBase = user.email.split('@')[0]
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .slice(0, 12);
    
    if (emailBase.length >= 3) {
      const suffix = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
      return `${emailBase}${suffix}`;
    }
  }

  // Fallback to user_id based
  const userId = user.user_id || user.id || Date.now().toString();
  return `user_${userId.slice(-8)}`;
}

export default {
  getProfileUrl,
  isValidProfileUrl,
  getDisplayHandle,
  parseProfilePath,
  generateHandleFromUser
};
